import ReactPaginate from 'react-paginate'
import styled from 'styled-components'

import type { getCategoryBySlug } from '@/graphql/HealthAcademyCategories'

import { Footer } from '@/components/elements/footer/Footer'
import { Container } from '@/components/shared/Container'
import { SEOMetaTags } from '@/components/shared/SEOMetaTags'
import { TrackedLink } from '@/components/shared/TrackedLink'

import { SubNavigation } from '@/elements/global/SubNavigation'
import { Header } from '@/elements/header/Header'

import { useTrackedRouter } from '@/lib/attribution/useTrackedRouter'

import type { ArticleCardArticle } from '../../Article/ArticleCard'
import { ArticlesByCategory } from '../../Article/ArticlesByCategory'

type NavigationCategory = {
  id: string
  categoryName: string
  categorySlug: string
}

// Derived from the GraphQL helper so the shape stays in step with the query.
type Subcategory = Awaited<ReturnType<typeof getCategoryBySlug>>

type CategoryContentProps = {
  categories?: NavigationCategory[]
  category: Subcategory
  parentCategory: {
    categorySlug: string
    categoryName: string
  }
  articles?: ArticleCardArticle[]
  articleCount: number
  slug?: string
  currentPage?: number
}

const StyledIntro = styled.div`
  text-align: center;
  margin: 80px 0;
  & h1 {
    margin-top: 24px;
    font-family: ${(props) => props.theme.v2.font.nantes};
  }
  @media screen and (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    margin: 40px 0 60px;
  }
`
const StyledBackLink = styled.div`
  a {
    text-transform: uppercase;
    font-weight: 500;
    font-size: 16px;
    letter-spacing: 2.4px;
    background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6.15674 2.33643L0.712016 7.78115C0.594859 7.8983 0.594858 8.08825 0.712016 8.20541L6.15674 13.6501' stroke='%23445DC3' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
    background-repeat: no-repeat;
    background-position: left 5px;
    padding-left: 24px;
    padding-top: 4px;
  }
  @media screen and (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    a {
      font-size: 14px;
    }
  }
`

const StyledPagination = styled.div`
  ul.pagination {
    display: flex;
    margin: 100px 0 0;
    padding: 0;
    justify-content: center;
    list-style-type: none;
    a {
      cursor: pointer;
      width: 22px;
      height: 25px;
      border-radius: 4px;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    li {
      margin: 0 10px;
      &.active a {
        color: ${(props) => props.theme.v2.interactive.bright};
        background-color: ${(props) => props.theme.v2.interactive.surface};
      }
      &.previous,
      &.next {
        position: relative;
        display: block;
        &:after {
          content: '';
          position: absolute;
          top: 8px;
          right: -15px;
          display: block;
          border-right: 1px solid ${(props) => props.theme.v2.interactive.bright};
          border-bottom: 1px solid ${(props) => props.theme.v2.interactive.bright};
          height: 8px;
          width: 8px;
          transform: rotate(-45deg);
        }
        a {
          width: auto;
          display: flex;
        }
        &.disabled {
          &:after {
            border-color: ${(props) => props.theme.v2.interactive.subdued};
          }
          a {
            color: ${(props) => props.theme.v2.interactive.subdued};
          }
        }
      }
      &.previous {
        &:after {
          right: auto;
          left: -15px;
          transform: rotate(135deg);
        }
      }
    }
  }
  @media screen and (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    ul.pagination {
      li {
        margin: 0 5px;
      }
      a {
        font-size: 14px !important;
      }
    }
  }
`
export const POSTS_PER_PAGE = 6

export const SubcategoryPage = ({
  categories,
  category,
  parentCategory,
  articles,
  articleCount,
  currentPage,
  slug,
}: CategoryContentProps) => {
  const { categoryName: parentCategoryName, categorySlug: parentCategorySlug } = parentCategory
  const { categoryName, id: categoryID, categorySlug, description, metadata } = category
  const router = useTrackedRouter()
  const totalPages: number = Math.ceil(articleCount / POSTS_PER_PAGE) | 0
  const initialPage: number = Number(currentPage)

  const pageChangeHandler = (page: { selected: number }) => {
    let pagePath = `/learn/category/${parentCategory.categorySlug}/${categorySlug}`
    if (page.selected) {
      pagePath = `/learn/category/${parentCategory.categorySlug}/${categorySlug}/p/${page.selected}`
    }

    if (page.selected !== initialPage) {
      void router.push({
        pathname: pagePath,
      })
    }
  }

  return (
    <>
      <SEOMetaTags metaData={metadata ?? undefined} />
      <Header />
      <SubNavigation title="Learn" categories={categories ?? []} selected={slug} />
      <main id="main-content">
        <StyledIntro>
          <Container>
            <StyledBackLink>
              <TrackedLink href={`/learn/category/${parentCategorySlug}`}>{parentCategoryName}</TrackedLink>
            </StyledBackLink>
            <h1>{categoryName}</h1>
            <p>{description}</p>
            <ArticlesByCategory
              categoryID={categoryID}
              categoryName={categoryName}
              categorySlug={parentCategorySlug}
              subCategorySlug={categorySlug}
              articles={articles}
              showHeader={false}
            />
            {totalPages > 1 && (
              <StyledPagination>
                <ReactPaginate
                  previousLabel={'Previous'}
                  nextLabel={'Next'}
                  breakLabel={'...'}
                  breakClassName={'break-me'}
                  activeClassName={'active'}
                  containerClassName={'pagination'}
                  initialPage={initialPage}
                  pageCount={totalPages}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={1}
                  onPageChange={pageChangeHandler}
                />
              </StyledPagination>
            )}
          </Container>
        </StyledIntro>
      </main>
      <Footer />
    </>
  )
}
